import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import api from '../../../services/api';
import { FiXCircle, FiSave } from '../../../utils/icons';
import { useT, useLocale } from '../../../i18n';

interface Plano {
  id: number;
  nome: string;
  descricao: string;
  max_usuarios: number;
  max_empresas_filhas: number;
  max_questionarios: number;
  preco_mensal: string;
}

interface Assinatura {
  id: number;
  empresa_id: number;
  plano_id: number;
  status: string;
  data_inicio: string;
  data_fim: string | null;
  plano_nome: string;
  max_usuarios: number;
  max_empresas_filhas: number;
  max_questionarios: number;
  preco_mensal: string;
}

interface Props {
  empresaId: number;
}

const statusVariant = (status: string) => {
  if (status === 'ativo') return 'success';
  if (status === 'trial') return 'warning';
  if (status === 'suspenso') return 'secondary';
  return 'danger';
};

const AssinaturaTab: React.FC<Props> = ({ empresaId }) => {
  const t = useT();
  const locale = useLocale();
  const formatLimit = (value: number) => (value === -1 ? t('companyRegistration.subscription.unlimited') : String(value));
  const formatPrice = (price: string) => {
    const n = parseFloat(price);
    if (n === 0) return t('companyRegistration.subscription.free');
    return n.toLocaleString(locale, { style: 'currency', currency: 'BRL' }) + t('companyRegistration.subscription.perMonth');
  };
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [assinaturaAtiva, setAssinaturaAtiva] = useState<Assinatura | null>(null);
  const [planoSelecionado, setPlanoSelecionado] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    const [planosRes, assRes] = await Promise.all([
      api.getPlanos(),
      api.getAssinaturas(empresaId),
    ]);

    if (planosRes.success && Array.isArray(planosRes.data)) {
      setPlanos(planosRes.data);
    }

    if (assRes.success && Array.isArray(assRes.data)) {
      const ativa = assRes.data.find(
        (a: Assinatura) => a.status === 'ativo' || a.status === 'trial'
      ) || null;
      setAssinaturaAtiva(ativa);
      if (ativa) setPlanoSelecionado(String(ativa.plano_id));
    }

    setLoading(false);
  }, [empresaId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSalvar = async () => {
    if (!planoSelecionado) return;
    setSaving(true);
    setMessage(null);
    const res = await api.createAssinatura({
      empresa_id: empresaId,
      plano_id: parseInt(planoSelecionado),
      status: 'ativo',
    });
    setSaving(false);
    if (res.success) {
      setMessage({ type: 'success', text: t('companyRegistration.subscription.assignSuccess') });
      fetchData();
    } else {
      const errText = res.data?.error || res.error || t('companyRegistration.subscription.assignError');
      setMessage({ type: 'danger', text: errText });
    }
  };

  const handleCancelar = async () => {
    if (!assinaturaAtiva) return;
    if (!window.confirm(t('companyRegistration.subscription.cancelConfirm'))) return;
    setCancelling(true);
    setMessage(null);
    const res = await api.cancelarAssinatura(assinaturaAtiva.id);
    setCancelling(false);
    if (res.success) {
      setMessage({ type: 'success', text: t('companyRegistration.subscription.cancelSuccess') });
      fetchData();
    } else {
      const errText = res.data?.error || res.error || t('companyRegistration.subscription.cancelError');
      setMessage({ type: 'danger', text: errText });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" /> {t('companyRegistration.subscription.loading')}
      </div>
    );
  }

  return (
    <div>
      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header>
          <strong>{t('companyRegistration.subscription.currentTitle')}</strong>
        </Card.Header>
        <Card.Body>
          {assinaturaAtiva ? (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p className="mb-1">
                    <strong>{t('companyRegistration.subscription.plan')}</strong> {assinaturaAtiva.plano_nome}
                  </p>
                  <p className="mb-1">
                    <strong>{t('companyRegistration.subscription.status')}</strong>{' '}
                    <Badge bg={statusVariant(assinaturaAtiva.status)}>
                      {assinaturaAtiva.status}
                    </Badge>
                  </p>
                  <p className="mb-1">
                    <strong>{t('companyRegistration.subscription.start')}</strong>{' '}
                    {new Date(assinaturaAtiva.data_inicio).toLocaleDateString(locale)}
                  </p>
                  {assinaturaAtiva.data_fim && (
                    <p className="mb-1">
                      <strong>{t('companyRegistration.subscription.validity')}</strong>{' '}
                      {new Date(assinaturaAtiva.data_fim).toLocaleDateString(locale)}
                    </p>
                  )}
                </Col>
                <Col md={6}>
                  <p className="mb-1">
                    <strong>{t('companyRegistration.subscription.users')}</strong> {formatLimit(assinaturaAtiva.max_usuarios)}
                  </p>
                  <p className="mb-1">
                    <strong>{t('companyRegistration.subscription.childCompanies')}</strong> {formatLimit(assinaturaAtiva.max_empresas_filhas)}
                  </p>
                  <p className="mb-1">
                    <strong>{t('companyRegistration.subscription.questionnaires')}</strong> {formatLimit(assinaturaAtiva.max_questionarios)}
                  </p>
                  <p className="mb-0">
                    <strong>{t('companyRegistration.subscription.price')}</strong> {formatPrice(assinaturaAtiva.preco_mensal)}
                  </p>
                </Col>
              </Row>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleCancelar}
                disabled={cancelling}
              >
                {cancelling ? <Spinner as="span" animation="border" size="sm" /> : <><FiXCircle /> {t('companyRegistration.subscription.cancelSubscription')}</>}
              </Button>
            </>
          ) : (
            <Alert variant="info" className="mb-0">
              {t('companyRegistration.subscription.noActive')}
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <strong>{assinaturaAtiva ? t('companyRegistration.subscription.changePlan') : t('companyRegistration.subscription.assignPlan')}</strong>
        </Card.Header>
        <Card.Body>
          <Table bordered hover size="sm" className="mb-3">
            <thead>
              <tr>
                <th></th>
                <th>{t('companyRegistration.subscription.colPlan')}</th>
                <th>{t('companyRegistration.subscription.colUsers')}</th>
                <th>{t('companyRegistration.subscription.colBranches')}</th>
                <th>{t('companyRegistration.subscription.colQuestionnaires')}</th>
                <th>{t('companyRegistration.subscription.colPrice')}</th>
              </tr>
            </thead>
            <tbody>
              {planos.map((p) => (
                <tr
                  key={p.id}
                  className={planoSelecionado === String(p.id) ? 'table-primary' : ''}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setPlanoSelecionado(String(p.id))}
                >
                  <td className="text-center">
                    <Form.Check
                      type="radio"
                      name="plano"
                      value={String(p.id)}
                      checked={planoSelecionado === String(p.id)}
                      onChange={() => setPlanoSelecionado(String(p.id))}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <strong>{p.nome}</strong>
                    <div className="text-muted small">{p.descricao}</div>
                  </td>
                  <td>{formatLimit(p.max_usuarios)}</td>
                  <td>{formatLimit(p.max_empresas_filhas)}</td>
                  <td>{formatLimit(p.max_questionarios)}</td>
                  <td>{formatPrice(p.preco_mensal)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Button
            variant="primary"
            onClick={handleSalvar}
            disabled={saving || !planoSelecionado}
          >
            {saving ? <Spinner as="span" animation="border" size="sm" /> : <><FiSave /> {t('companyRegistration.subscription.savePlan')}</>}
          </Button>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AssinaturaTab;
